import { useState } from "react";
import { useAuth } from "../components/AuthContext";
import { useLiveData, useActionWithRefresh } from "../hooks/useLiveData";
import { getAllFeatures } from "../services/features";
import { getAllBugs } from "../services/bugs";
import { getAllVersions, deployVersion, getDeployQueue } from "../services/versions";
import { reconcilePipelineState } from "../services/reconciler";
import {
  getPipelineState,
  getRecentExecutions,
  approveStage,
  rejectStage,
  enableGate,
  disableGate,
  stopPipeline,
  retryStage,
  type PipelineStageStatus,
  type PipelineExecution,
} from "../services/pipeline";
import type { Version } from "../types";

export default function Dashboard() {
  const { can } = useAuth();
  const [showDeployModal, setShowDeployModal] = useState(false);

  // Live pipeline state (polls every 8s)
  const pipeline = useLiveData<PipelineStageStatus[]>({
    fetcher: async () => {
      await reconcilePipelineState();
      return getPipelineState();
    },
    interval: 8000,
  });

  // Live deploy queue
  const queue = useLiveData<Version[]>({ fetcher: getDeployQueue, interval: 8000 });

  // Deployable versions
  const deployable = useLiveData<Version[]>({
    fetcher: async () => {
      const versions = await getAllVersions();
      return versions.filter((v) => v.status === "created" || v.status === "failed");
    },
    interval: 15000,
  });

  // Recent executions
  const executions = useLiveData<PipelineExecution[]>({
    fetcher: () => getRecentExecutions(5),
    interval: 10000,
  });

  // Stats
  const stats = useLiveData<{ openFeatures: number; openBugs: number }>({
    fetcher: async () => {
      const [features, bugs] = await Promise.all([getAllFeatures(), getAllBugs()]);
      return {
        openFeatures: features.filter((f) => f.status === "open" || f.status === "in-progress").length,
        openBugs: bugs.filter((b) => b.status === "open" || b.status === "in-progress").length,
      };
    },
    interval: 30000,
  });

  const refreshAll = async () => {
    await Promise.all([pipeline.refresh(), queue.refresh(), deployable.refresh(), executions.refresh()]);
  };

  const { busy, actionError, execute, clearError } = useActionWithRefresh(refreshAll);

  const stages = pipeline.data || [];
  const currentExecutionId = stages.find((s) => s.latestExecution)?.latestExecution?.pipelineExecutionId || "";

  // Categorize stages for the pipeline flow
  const buildStage = stages.find((s) => s.stageName === "Build");
  const deployDev = stages.find((s) => s.stageName === "Deploy_Dev");
  const approveBeta = stages.find((s) => s.stageName === "Approve_Beta");
  const deployBeta = stages.find((s) => s.stageName === "Deploy_Beta");
  const approveProd = stages.find((s) => s.stageName === "Approve_Prod");
  const deployProd = stages.find((s) => s.stageName === "Deploy_Prod");

  function statusClass(status: string): string {
    switch (status) {
      case "Succeeded": return "succeeded";
      case "InProgress": return "in-progress";
      case "Failed": return "failed";
      default: return "idle";
    }
  }

  function statusIcon(status: string): string {
    switch (status) {
      case "Succeeded": return "✓";
      case "InProgress": return "⟳";
      case "Failed": return "✕";
      default: return "—";
    }
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h2>POS System Pipeline</h2>
            <p>Deploy versions, control gates, approve promotions.</p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
            {pipeline.lastUpdated && (
              <span className="live-indicator">
                <span className="live-dot" />
                Live · {pipeline.lastUpdated.toLocaleTimeString()}
              </span>
            )}
            {can("pipeline:stop") && currentExecutionId && stages.some((s) => s.status === "InProgress") && (
              <button className="mini-btn rollback" onClick={() => execute("stop", () => stopPipeline(currentExecutionId, "Stopped via DevPortal"))} disabled={busy === "stop"}>
                ⏹ Stop
              </button>
            )}
            {can("pipeline:trigger") && deployable.data && deployable.data.length > 0 && (
              <button className="btn btn-primary" onClick={() => setShowDeployModal(!showDeployModal)}>
                ▶ Deploy Version
              </button>
            )}
            <button className="ctrl-btn refresh" onClick={refreshAll} disabled={pipeline.loading}>
              {pipeline.loading ? "⟳" : "↻"}
            </button>
          </div>
        </div>
      </div>

      {(actionError || pipeline.error) && (
        <div className="error-banner">
          ⚠️ {actionError || pipeline.error}
          <button onClick={clearError} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: "1rem" }}>✕</button>
        </div>
      )}

      {/* Deploy modal */}
      {showDeployModal && deployable.data && (
        <div className="card animate-slide-down" style={{ marginBottom: "1.5rem" }}>
          <h4 style={{ marginBottom: "0.75rem" }}>Select version to deploy:</h4>
          {deployable.data.map((v) => (
            <div key={v.id} style={{ display: "flex", alignItems: "center", gap: "0.75rem", padding: "0.5rem 0", borderBottom: "1px solid var(--border)" }}>
              <strong>v{v.version}</strong>
              <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{v.commits.length} commits</span>
              <code style={{ fontSize: "0.75rem" }}>{v.targetCommitId?.substring(0, 7)}</code>
              {v.status === "failed" && <span className="badge badge-danger">retry</span>}
              <button
                className="mini-btn deploy"
                style={{ marginLeft: "auto" }}
                onClick={() => { execute(`deploy-${v.id}`, () => deployVersion(v.id)); setShowDeployModal(false); }}
                disabled={busy === `deploy-${v.id}`}
              >
                {busy === `deploy-${v.id}` ? "⟳" : "▶"} Deploy
              </button>
            </div>
          ))}
          {deployable.data.length === 0 && (
            <p style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>No versions ready. Create one in Versions.</p>
          )}
        </div>
      )}

      {/* Pipeline Flow — horizontal cards with arrows */}
      <div className="pipeline-flow">
        {/* Build */}
        {buildStage && (
          <>
            <div className={`pipeline-block ${statusClass(buildStage.status)}`}>
              <div className="pb-header">
                <span className="pb-icon">⚙️</span>
                <span className="pb-name">Build</span>
              </div>
              <div className="pb-status">
                <span className={`pb-status-dot ${statusClass(buildStage.status)}`}>{statusIcon(buildStage.status)}</span>
                <span>{buildStage.status === "Succeeded" ? "All actions succeeded" : buildStage.status}</span>
              </div>
              {buildStage.status === "Failed" && buildStage.latestExecution && can("pipeline:rollback") && (
                <button className="mini-btn deploy" style={{ marginTop: "0.5rem" }} onClick={() => execute("retry-Build", () => retryStage("Build", buildStage.latestExecution!.pipelineExecutionId))} disabled={busy === "retry-Build"}>
                  ↻ Retry
                </button>
              )}
            </div>
            <div className="pipeline-arrow">→</div>
          </>
        )}

        {/* Deploy Dev */}
        {deployDev && (
          <>
            <div className={`pipeline-block ${statusClass(deployDev.status)}`}>
              <div className="pb-header">
                <span className="pb-icon">🚀</span>
                <span className="pb-name">Deploy Dev</span>
              </div>
              <div className="pb-status">
                <span className={`pb-status-dot ${statusClass(deployDev.status)}`}>{statusIcon(deployDev.status)}</span>
                <span>{deployDev.status === "Succeeded" ? "Deployed" : deployDev.status}</span>
              </div>
              {deployDev.status === "Failed" && deployDev.latestExecution && can("pipeline:rollback") && (
                <button className="mini-btn deploy" style={{ marginTop: "0.5rem" }} onClick={() => execute("retry-Deploy_Dev", () => retryStage("Deploy_Dev", deployDev.latestExecution!.pipelineExecutionId))} disabled={busy === "retry-Deploy_Dev"}>
                  ↻ Retry
                </button>
              )}
            </div>
            <div className="pipeline-arrow">→</div>
          </>
        )}

        {/* Approve Beta Gate */}
        {approveBeta && (
          <>
            <div className={`pipeline-gate ${statusClass(approveBeta.status)}`}>
              <div className="pg-icon">
                {approveBeta.status === "InProgress" ? "🔒" : approveBeta.status === "Succeeded" ? "🔓" : "⚪"}
              </div>
              <div className="pg-label">Beta Gate</div>
              {approveBeta.status === "InProgress" && (() => {
                const action = approveBeta.actionStates.find((a) => a.latestExecution?.token);
                if (!action?.latestExecution?.token || !can("pipeline:approve-frontend")) return null;
                return (
                  <div className="pg-actions">
                    <button className="gate-btn approve" onClick={() => execute("approve-beta", () => approveStage("Approve_Beta", action.actionName, action.latestExecution!.token!))} disabled={busy === "approve-beta"}>
                      ✓
                    </button>
                    <button className="gate-btn reject" onClick={() => execute("reject-beta", () => rejectStage("Approve_Beta", action.actionName, action.latestExecution!.token!))} disabled={busy === "reject-beta"}>
                      ✕
                    </button>
                  </div>
                );
              })()}
              {can("pipeline:gates") && (
                <button
                  className={`gate-toggle-sm ${approveBeta.inboundTransitionState?.enabled !== false ? "open" : "closed"}`}
                  onClick={() => execute("gate-Approve_Beta", () => approveBeta.inboundTransitionState?.enabled !== false ? disableGate("Approve_Beta") : enableGate("Approve_Beta"))}
                  disabled={busy === "gate-Approve_Beta"}
                  title={approveBeta.inboundTransitionState?.enabled !== false ? "Disable gate" : "Enable gate"}
                >
                  {approveBeta.inboundTransitionState?.enabled !== false ? "●" : "○"}
                </button>
              )}
            </div>
            <div className="pipeline-arrow">→</div>
          </>
        )}

        {/* Deploy Beta */}
        {deployBeta && (
          <>
            <div className={`pipeline-block ${statusClass(deployBeta.status)}`}>
              <div className="pb-header">
                <span className="pb-icon">🚀</span>
                <span className="pb-name">Deploy Beta</span>
              </div>
              <div className="pb-status">
                <span className={`pb-status-dot ${statusClass(deployBeta.status)}`}>{statusIcon(deployBeta.status)}</span>
                <span>{deployBeta.status === "Succeeded" ? "Deployed" : deployBeta.status}</span>
              </div>
              {deployBeta.status === "Failed" && deployBeta.latestExecution && can("pipeline:rollback") && (
                <button className="mini-btn deploy" style={{ marginTop: "0.5rem" }} onClick={() => execute("retry-Deploy_Beta", () => retryStage("Deploy_Beta", deployBeta.latestExecution!.pipelineExecutionId))} disabled={busy === "retry-Deploy_Beta"}>
                  ↻ Retry
                </button>
              )}
            </div>
            <div className="pipeline-arrow">→</div>
          </>
        )}

        {/* Approve Prod Gate */}
        {approveProd && (
          <>
            <div className={`pipeline-gate ${statusClass(approveProd.status)}`}>
              <div className="pg-icon">
                {approveProd.status === "InProgress" ? "🔒" : approveProd.status === "Succeeded" ? "🔓" : "⚪"}
              </div>
              <div className="pg-label">Prod Gate</div>
              {approveProd.status === "InProgress" && (() => {
                const action = approveProd.actionStates.find((a) => a.latestExecution?.token);
                if (!action?.latestExecution?.token || !can("pipeline:approve-frontend")) return null;
                return (
                  <div className="pg-actions">
                    <button className="gate-btn approve" onClick={() => execute("approve-prod", () => approveStage("Approve_Prod", action.actionName, action.latestExecution!.token!))} disabled={busy === "approve-prod"}>
                      ✓
                    </button>
                    <button className="gate-btn reject" onClick={() => execute("reject-prod", () => rejectStage("Approve_Prod", action.actionName, action.latestExecution!.token!))} disabled={busy === "reject-prod"}>
                      ✕
                    </button>
                  </div>
                );
              })()}
              {can("pipeline:gates") && (
                <button
                  className={`gate-toggle-sm ${approveProd.inboundTransitionState?.enabled !== false ? "open" : "closed"}`}
                  onClick={() => execute("gate-Approve_Prod", () => approveProd.inboundTransitionState?.enabled !== false ? disableGate("Approve_Prod") : enableGate("Approve_Prod"))}
                  disabled={busy === "gate-Approve_Prod"}
                  title={approveProd.inboundTransitionState?.enabled !== false ? "Disable gate" : "Enable gate"}
                >
                  {approveProd.inboundTransitionState?.enabled !== false ? "●" : "○"}
                </button>
              )}
            </div>
            <div className="pipeline-arrow">→</div>
          </>
        )}

        {/* Deploy Prod */}
        {deployProd && (
          <div className={`pipeline-block ${statusClass(deployProd.status)}`}>
            <div className="pb-header">
              <span className="pb-icon">🏁</span>
              <span className="pb-name">Deploy Prod</span>
            </div>
            <div className="pb-status">
              <span className={`pb-status-dot ${statusClass(deployProd.status)}`}>{statusIcon(deployProd.status)}</span>
              <span>{deployProd.status === "Succeeded" ? "Live" : deployProd.status}</span>
            </div>
            {deployProd.status === "Failed" && deployProd.latestExecution && can("pipeline:rollback") && (
              <button className="mini-btn deploy" style={{ marginTop: "0.5rem" }} onClick={() => execute("retry-Deploy_Prod", () => retryStage("Deploy_Prod", deployProd.latestExecution!.pipelineExecutionId))} disabled={busy === "retry-Deploy_Prod"}>
                ↻ Retry
              </button>
            )}
          </div>
        )}
      </div>

      {/* Deploy Queue */}
      {queue.data && queue.data.length > 0 && (
        <div className="card animate-fade-in" style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>Deploying</h3>
          {queue.data.map((v) => (
            <div key={v.id} className="deploy-queue-item deploying">
              <span className="dq-status">{v.status === "deploying" ? "🔄" : "⏳"}</span>
              <span className="dq-version">v{v.version}</span>
              <span className="dq-stage">→ {v.stage}</span>
              <span className="dq-commits">{v.commits.length} commits</span>
              {v.pipelineExecutionId && <code className="dq-exec">{v.pipelineExecutionId.substring(0, 8)}</code>}
            </div>
          ))}
        </div>
      )}

      {/* Recent Executions */}
      {executions.data && executions.data.length > 0 && (
        <div className="card" style={{ marginTop: "1.5rem" }}>
          <h3 style={{ marginBottom: "0.75rem" }}>Recent Executions</h3>
          <div style={{ fontSize: "0.85rem" }}>
            {executions.data.map((exec) => (
              <div key={exec.pipelineExecutionId} style={{ display: "flex", gap: "1rem", padding: "0.4rem 0", borderBottom: "1px solid var(--border)", alignItems: "center" }}>
                <span className={`badge ${exec.status === "Succeeded" ? "badge-success" : exec.status === "Failed" ? "badge-danger" : "badge-warning"}`}>
                  {exec.status}
                </span>
                <code>{exec.pipelineExecutionId.substring(0, 12)}</code>
                <span style={{ color: "var(--text-muted)" }}>{new Date(exec.startTime).toLocaleString()}</span>
                {exec.sourceRevisions?.[0] && (
                  <code style={{ marginLeft: "auto", fontSize: "0.75rem" }}>{exec.sourceRevisions[0].revisionId.substring(0, 7)}</code>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="card-grid" style={{ marginTop: "1.5rem" }}>
        <div className="stat-card">
          <div className="label">Open Features</div>
          <div className="value" style={{ color: (stats.data?.openFeatures || 0) > 0 ? "var(--primary)" : "inherit" }}>
            {stats.data?.openFeatures ?? "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Open Bugs</div>
          <div className="value" style={{ color: (stats.data?.openBugs || 0) > 0 ? "var(--danger)" : "inherit" }}>
            {stats.data?.openBugs ?? "—"}
          </div>
        </div>
      </div>
    </div>
  );
}
